import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import {
  NUMBER_FORMATS,
  TOTAL_LABEL,
  getSummedLabels,
  getTotalLabelColumn,
  parseMeasure,
  toSpreadsheetNumber,
  totalMeasure
} from './spreadsheetTotals';

const normalizeRow = (row, delimiter = ', ') => {
  if (!row) return {};
  return Object?.fromEntries(
    Object?.entries(row)?.map(([key, value]) => {
      if (Array.isArray(value)) {
        if (value?.length == 0) return [key, '-'];

        if (value?.length && typeof value?.[0] === 'object') {
          return [key, value?.map((o) => JSON.stringify(o))?.join('; ')];
        }

        return [key, value?.map((x) => (x == null ? '' : x))?.join(delimiter)];
      }
      if (value && typeof value === 'object' && !(value instanceof Date)) {
        return [key, JSON.stringify(value)];
      }

      return [key, value ?? '-'];
    })
  );
};

// Measure cells become numbers with a number format, so Excel can SUM them.
const toNumericMeasures = (row, measures) => {
  const result = { ...row };

  measures.forEach(({ kind }, label) => {
    const measure = parseMeasure(kind, result[label]);
    if (measure !== null) result[label] = toSpreadsheetNumber(kind, measure);
  });

  return result;
};

const appendTotalsRow = (worksheet, rows, headers, measures) => {
  const summed = new Set(getSummedLabels(measures));
  if (!summed.size) return;

  const labelColumn = getTotalLabelColumn(headers, measures);
  const totalsRowIndex = rows.length + 1;

  headers.forEach((label, columnIndex) => {
    const address = XLSX.utils.encode_cell({ r: totalsRowIndex, c: columnIndex });

    if (summed.has(label)) {
      const measure = measures.get(label);
      const column = XLSX.utils.encode_col(columnIndex);

      // The report's own total is written as a value: a SUM over overlapping rows would overstate it.
      worksheet[address] = {
        t: 'n',
        v: toSpreadsheetNumber(measure.kind, totalMeasure(rows, label, measure)),
        ...(measure.reportTotal === undefined && {
          f: `SUM(${column}2:${column}${totalsRowIndex})`
        }),
        z: NUMBER_FORMATS[measure.kind]
      };
    } else if (label === labelColumn) {
      worksheet[address] = { t: 's', v: TOTAL_LABEL };
    }
  });

  const range = XLSX.utils.decode_range(worksheet['!ref']);
  range.e.r = totalsRowIndex;
  worksheet['!ref'] = XLSX.utils.encode_range(range);
};

// measures: Map of header label -> { kind, isSummable, reportTotal } (see describeMeasures). Without it the
// sheet is plain text, as for every non-report table.
const exportToExcel = (data, fileName, { measures = new Map() } = {}) => {
  if (!data.length) {
    return;
  }
  const flat = data?.map((d) => normalizeRow(d));

  const headers = Object.keys(flat?.[0]);

  const worksheet = XLSX.utils.json_to_sheet(
    flat.map((row) => toNumericMeasures(row, measures)),
    { header: headers }
  );

  measures.forEach(({ kind }, label) => {
    const columnIndex = headers.indexOf(label);
    if (columnIndex === -1) return;

    flat.forEach((_, rowIndex) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: columnIndex })];
      if (cell?.t === 'n') cell.z = NUMBER_FORMATS[kind];
    });
  });

  appendTotalsRow(worksheet, flat, headers, measures);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });

  saveAs(dataBlob, `${fileName}.xlsx`);
};

export default exportToExcel;
